'use client';

import { useEffect, useRef, useState } from 'react';

interface Props {
  /* Phrases to cycle through. Types one in left-to-right, holds it,
     removes it word by word from the END (LIFO — the last word typed
     is the first one removed), then types the next phrase. */
  phrases: string[];
  /* Per-character typing cadence in ms. Default 75 — slower than
     the v1 60ms so the reveal reads as deliberate, not jittery. */
  typingMs?: number;
  /* Per-word cadence for the reverse pass. Default 220 — quick
     enough that a 4-word phrase clears in under a second but slow
     enough that each word drop reads as a discrete beat. */
  wordMs?: number;
  /* How long the fully-typed phrase holds before the reverse pass
     starts. Default 3500ms per spec ("3 to 3.5 seconds"). */
  holdMs?: number;
}

/* Drops the trailing word from a string. "sauna and deep sleep" →
   "sauna and deep" → "sauna and" → "sauna" → "". Whitespace between
   the removed word and the previous word is dropped with it. */
const removeLastWord = (s: string): string => {
  const trimmed = s.trimEnd();
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace < 0) return '';
  return trimmed.slice(0, lastSpace);
};

/* Looped typewriter — types a phrase character-by-character, holds
   it, then unwinds the phrase one word at a time from the END (LIFO),
   then types the next phrase. */
const TypewriterText = ({
  phrases,
  typingMs = 75,
  wordMs = 220,
  holdMs = 3500,
}: Props) => {
  const [text, setText] = useState('');
  const [reducedMotion, setReducedMotion] = useState(false);
  const indexRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  /* Keys the loop on phrase CONTENT, not array identity, so a caller that
     rebuilds the array each render cannot restart the animation. */
  const phrasesKey = phrases.join(' ');

  useEffect(() => {
    if (phrases.length === 0) return;

    /* Reduced motion: one phrase, held. The loop is a continuous animation
       with no pause control, which is exactly what the preference asks us not
       to run — and the placeholder still reads correctly standing still. */
    if (reducedMotion) {
      setText(phrases[0]);
      return;
    }

    let cancelled = false;
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timeoutRef.current = setTimeout(resolve, ms);
      });

    const run = async () => {
      while (!cancelled) {
        const phrase = phrases[indexRef.current];

        /* Type in, character by character. */
        for (let i = 1; i <= phrase.length; i++) {
          if (cancelled) return;
          setText(phrase.slice(0, i));
          await wait(typingMs);
        }
        if (cancelled) return;

        /* Hold the fully-typed phrase. */
        await wait(holdMs);
        if (cancelled) return;

        /* Reverse-out: peel one word off the END per tick (LIFO —
           the last word typed is the first removed). */
        let current = phrase;
        while (current !== '') {
          current = removeLastWord(current);
          if (cancelled) return;
          setText(current);
          await wait(wordMs);
        }
        if (cancelled) return;

        /* Brief beat before the next phrase starts typing. */
        await wait(220);
        indexRef.current = (indexRef.current + 1) % phrases.length;
      }
    };

    run();
    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [phrasesKey, typingMs, wordMs, holdMs, reducedMotion]);

  return <>{text}</>;
};

export default TypewriterText;
